<template>
  <footer v-if="built" class="data-stamp">
    Stats updated {{ fmt(built) }}<span class="ds-ago"> ({{ ago(built) }})</span>
    <template v-if="latestId">
      · <a :href="'?tournament=' + latestId" title="The newest finished tournament these stats include (it opens that bracket)">latest tournament included: {{ fmt(latestId, built) }}</a>
    </template>
    <template v-if="chatBuilt">
      <br><span title="Chat is rebuilt once a day, from the chat logs up to the day before">Chat updated {{ fmt(chatBuilt) }}<span class="ds-ago"> ({{ ago(chatBuilt) }})</span> · logs through {{ day(chatLastDay) }}</span>
    </template>
  </footer>
</template>

<script>
// The "Stats updated …" line at the foot of every page. The stats come from the
// refresh box's newest build (see lib/dataStore.js), which lands a few minutes after
// each tournament finishes, so this says when that build was made and links the newest
// tournament it covers. That answers "is this up to date?", e.g. why a title won in
// the last tournament isn't in a history yet. It reads the page's own manifest, so it
// describes exactly the data the page is showing.
// Chat pages (`chat`) add a second line: chat is rebuilt once a day, not per
// tournament, so the stats time would overstate how fresh their data is.
import { dataInfo, loadData } from '../lib/dataStore.js';

export default {
  name: 'DataStamp',
  props: { chat: Boolean },
  data() { return { built: null, latestId: null, chatBuilt: null, chatLastDay: '', now: Date.now(), timer: null }; },
  async created() {
    const info = await dataInfo().catch(() => null);
    const built = info && Date.parse(info.built);
    if (!built) return;
    this.built = built;
    this.latestId = info.latestId;
    this.timer = setInterval(() => { this.now = Date.now(); }, 60000);  // keep "N min ago" honest in an open tab
    if (this.chat) {
      const meta = await loadData('chatMeta.json').catch(() => null);
      if (meta && Date.parse(meta.builtAt)) { this.chatBuilt = Date.parse(meta.builtAt); this.chatLastDay = meta.lastDay || ''; }
    }
  },
  beforeUnmount() { clearInterval(this.timer); },
  methods: {
    // Local date + time ("9/23/2026 at 2:07 PM"); just the time when it's the same day as `sameDayAs`.
    fmt(ms, sameDayAs) {
      const d = new Date(ms), time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      return sameDayAs && d.toDateString() === new Date(sameDayAs).toDateString() ? time : d.toLocaleDateString() + ' at ' + time;
    },
    // A log day ("2026-09-23") in the local date format, read as a calendar date (no UTC shift).
    day(ymd) {
      const [y, m, d] = String(ymd).split('-').map(Number);
      return y && m && d ? new Date(y, m - 1, d).toLocaleDateString() : ymd;
    },
    ago(ms) {
      const min = Math.round((this.now - ms) / 60000);
      if (min < 1) return 'just now';
      if (min < 60) return min + ' min ago';
      const h = Math.round(min / 60);
      return h < 48 ? h + (h === 1 ? ' hour ago' : ' hours ago') : Math.round(h / 24) + ' days ago';
    },
  },
};
</script>

<style scoped>
.data-stamp {
  /* A shrink-wrapped block, centered by its margins: the bracket page sets
     #app { text-align: left }, so inline-block centering doesn't hold there. */
  display: block;
  width: fit-content;
  max-width: calc(100% - 24px);
  box-sizing: border-box;
  margin: 18px auto 22px;
  text-align: center;
  padding: 4px 12px;
  border-radius: 10px;
  background: rgba(8, 18, 30, 0.72);
  color: #c9dcee;
  font-size: 12px;
  line-height: 1.5;
}
.data-stamp a { color: #9fd0ff; text-decoration: none; }
.data-stamp a:hover { text-decoration: underline; }
.ds-ago { color: #8fa9c2; }
</style>
